import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SpaceFileReadModel, useGetSpacesBySpaceIdFilesQuery, usePostSpacesBySpaceIdFilesMutation } from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
} from '@client/toolkit';

import { SpaceFileDeleteModal } from './space-file-delete-modal.component';
import { SpaceFileItem } from './space-file-item.component';

export interface SpaceFilesCardProps {
  className?: string;
  spaceId?: string;
}

export function SpaceFilesCard({ className, spaceId }: SpaceFilesCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const pageSize = 20;
  const [page, setPage] = useState(0);

  const {
    data: spaceFilesData,
    isFetching,
    isLoading,
  } = useGetSpacesBySpaceIdFilesQuery(
    {
      spaceId: spaceId ?? '',
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
    },
    { skip: !spaceId },
  );

  const spaceFiles = useMemo(() => spaceFilesData?.items ?? [], [spaceFilesData?.items]);
  const totalCount = spaceFiles != null && spaceFiles.length > 0 ? spaceFiles.length : 0;

  const hasSpaceFiles = spaceFiles != null && spaceFiles.length <= 0;
  const isListEmpty = useMemo(() => hasSpaceFiles, [hasSpaceFiles]);

  /* Delete */

  const [deletingItem, setDeletingItem] = useState<boolean>(false);
  const [deletingItemModel, setDeletingItemModel] = useState<SpaceFileReadModel>();

  const handleOpenDeleteItem = (model: SpaceFileReadModel) => {
    setDeletingItem(true);
    setDeletingItemModel(model);
  };

  const handleCloseDeleteItem = () => {
    setDeletingItem(false);
  };

  const handleDeleteItemFulfilled = () => {
    setDeletingItem(false);
  };

  /* --------- */

  /* Upload */

  const [postFile, { isLoading: isUploading }] = usePostSpacesBySpaceIdFilesMutation();

  const inputRef = useRef<HTMLInputElement>(null);
  const handleClickUpload = useCallback(() => inputRef.current?.click(), [inputRef]);
  const handleChangeInput = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (spaceId == null) return;

      const fileContent = event.target.files?.[0];
      if (!fileContent) return;

      const body = new FormData();
      body.append('File', fileContent);

      await postFile({ spaceId: spaceId, body: body as any });

      if (inputRef.current?.files) {
        inputRef.current.files = null;
      }
      if (inputRef.current?.value) {
        inputRef.current.value = '';
      }
    },
    [spaceId, postFile],
  );

  /* ---------- */

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex-1" variant="dense">
            {tGlobal('entity.spaceFile', { context: 'plural' })}
          </CardTitle>
          <CardActions>
            <Button
              size="sm"
              color="primary"
              onClick={handleClickUpload}
              disabled={isFetching}
              progressing={isUploading}
            >
              <input ref={inputRef} type="file" className="hidden" onChange={handleChangeInput} />

              {tGlobal('upload')}
            </Button>
          </CardActions>
        </CardHeader>

        <CardContent variant="fill">
          <Table className="overflow-hidden w-full mb-4" size="xs" layout="fixed">
            {!isLoading && isListEmpty && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>
                  {tCommon('noEntitySpecified', { entity: tGlobal('entity.spaceFile', { context: 'plural' }).toLowerCase() })}
                </EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{tGlobal('entity.file')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>

            <TableBody>
              {isLoading && Array.from(Array(3).keys()).map((x, idx) => <SpaceFileItem key={x} isFetching />)}

              {!isLoading &&
                spaceFiles?.map((file) => (
                  <SpaceFileItem
                    key={file.id}
                    spaceFile={file}
                    isFetching={isFetching}
                    onClickDelete={handleOpenDeleteItem}
                  />
                ))}
            </TableBody>
          </Table>

          <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
        </CardContent>
      </Card>

      <SpaceFileDeleteModal
        open={deletingItem}
        spaceFile={deletingItemModel}
        onCancel={handleCloseDeleteItem}
        onDeleteFulfilled={handleDeleteItemFulfilled}
      />
    </>
  );
}
